/*
 * /classes/:slug/submissions/:subId? — bài nộp của cả lớp. subId = id bài đang mở
 * (chi tiết: vote + bình luận). Danh sách: theo Product (có lọc) hoặc 10 bài mới nhất.
 */
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Icon } from "@/components/icon"
import { EmptyState, ErrorState, Page, PageHeader, usePageTitle } from "@/components/page"
import { useClass } from "@/app/ClassLayout"
import { api } from "@/lib/api"
import { MeTag, StatusBadge, SubmissionAvatar } from "./submissions/shared"
import { SubmissionDetail } from "./submissions/detail"

const ALL = "__all"

function SubmissionCard({ item, to }: { item: any; to: string }) {
  return (
    <Link to={to} className="group block">
      <Card size="sm" className="transition-[transform,box-shadow] group-hover:-translate-y-0.5 group-hover:shadow-card-hover">
        <CardContent className="flex items-center gap-3 sm:gap-4">
          <SubmissionAvatar author={item.author} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <strong className="truncate font-heading text-base text-ink">{item.product_title || item.product_name}</strong>
            {item.product_title && <span className="truncate text-sm text-muted-foreground">{item.product_name}</span>}
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {item.author?.full_name}
              <MeTag show={item.author?.is_me} />· Phiên bản {item.version}
            </span>
            <span className="flex flex-wrap items-center gap-3 pt-1 sm:hidden">
              <StatusBadge status={item.status} />
              <Stats item={item} />
            </span>
          </div>
          <div className="hidden flex-col items-end gap-2 sm:flex">
            <StatusBadge status={item.status} />
            <Stats item={item} />
          </div>
          <Icon name="arrow-right" className="size-5 transition-transform group-hover:translate-x-1" />
        </CardContent>
      </Card>
    </Link>
  )
}

function Stats({ item }: { item: any }) {
  return (
    <span className="flex items-center gap-3 text-sm text-muted-foreground">
      <span className="flex items-center gap-1" title="Điểm vote">
        <Icon name="heart" className="size-4" />
        {item.score > 0 ? `+${item.score}` : item.score}
      </span>
      <span className="flex items-center gap-1" title="Bình luận">
        <Icon name="chat" className="size-4" />
        {item.comment_count}
      </span>
    </span>
  )
}

function Board() {
  const { slug, base } = useClass()
  const reduce = useReducedMotion()
  const [list, setList] = useState<any>()
  const [error, setError] = useState("")
  const [tab, setTab] = useState<"product" | "recent">("product")
  const [productFilter, setProductFilter] = useState(ALL)

  const load = () => {
    setError("")
    api(`/api/classes/${encodeURIComponent(slug)}/submissions`)
      .then(setList)
      .catch((e: any) => setError(e.message))
  }
  useEffect(() => { load() }, [slug])

  const header = <PageHeader eyebrow="Submissions" title="Bài nộp của cả lớp" description="Xem bài của các bạn, để lại bình luận và vote cho từng bài." />
  if (error)
    return (
      <>
        {header}
        <ErrorState message={error} onRetry={load} />
      </>
    )
  if (!list)
    return (
      <>
        {header}
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 rounded-[20px]" />
          ))}
        </div>
      </>
    )

  const submissions: any[] = list.submissions || []
  if (!submissions.length)
    return (
      <>
        {header}
        <EmptyState mascot="elephant-happy" title="Chưa có bài nộp nào" description="Khi các bạn nộp bài, chúng sẽ xuất hiện ở đây." />
      </>
    )

  const products = Array.from(new Map<string, string>(submissions.map((s) => [String(s.product_id), String(s.product_name)])).entries()).map(([value, label]) => ({ value, label }))
  const items = [{ value: ALL, label: "Tất cả Product" }, ...products]
  const visible =
    tab === "recent" ? submissions.slice(0, 10) : productFilter !== ALL ? submissions.filter((s) => String(s.product_id) === productFilter) : submissions

  return (
    <>
      {header}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={(v) => setTab(v as "product" | "recent")}>
          <TabsList className="h-10! w-full sm:w-fit" aria-label="Cách xem bài nộp">
            <TabsTrigger value="product" className="px-4">Theo Product</TabsTrigger>
            <TabsTrigger value="recent" className="px-4">10 bài mới nhất</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "product" && (
          <Select items={items} value={productFilter} onValueChange={(v) => setProductFilter(String(v ?? ALL))}>
            <SelectTrigger aria-label="Lọc theo Product" className="h-10 w-full sm:w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {items.map((it) => (
                <SelectItem key={it.value} value={it.value}>
                  {it.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {visible.length ? (
        <motion.ul
          key={`${tab}-${productFilter}`}
          className="flex flex-col gap-3"
          initial={reduce ? false : "hidden"}
          animate="shown"
          variants={{ hidden: {}, shown: { transition: { staggerChildren: 0.04 } } }}
        >
          {visible.map((item) => (
            <motion.li key={item.id} variants={{ hidden: reduce ? {} : { y: 8 }, shown: { y: 0 } }}>
              <SubmissionCard item={item} to={`${base}/submissions/${encodeURIComponent(item.id)}`} />
            </motion.li>
          ))}
        </motion.ul>
      ) : (
        <EmptyState icon="file" title="Chưa có bài nộp nào cho Product này." />
      )}
    </>
  )
}

export default function SubmissionsPage() {
  usePageTitle("Submissions", "Bài nộp của cả lớp: xem, vote và bình luận.")
  const { slug, base } = useClass()
  const { subId } = useParams()
  return <Page className="max-w-4xl">{subId ? <SubmissionDetail slug={slug} id={subId} backTo={`${base}/submissions`} /> : <Board />}</Page>
}
